import axios from "axios";
import { API_BASE_URL } from "../../Services/api_url";

/**
 * Self-contained API layer for the two portals.
 * Token is attached globally by installHttp() (see app/auth/http.ts).
 * Endpoints mirror the Spring backend's /api/<entity>/{all,create,...} contract.
 */

async function getList(path: string): Promise<any[]> {
  try {
    const res = await axios.get<any[]>(`${API_BASE_URL}${path}`);
    return res?.data ?? [];
  } catch (e) {
    console.error(`GET ${path} failed`, e);
    return [];
  }
}

export const fetchProducts   = () => getList("/api/product/all");
export const fetchCategories = () => getList("/api/category/all");
export const fetchOrders     = () => getList("/api/orders/all");
export const fetchInventory  = () => getList("/api/inventory/all");
export const fetchUsers      = () => getList("/api/user/all");
/** QA #1: BUYER accounts of the signed-in distributor's tenant only (server-filtered). */
export const fetchCustomers  = () => getList("/api/user/customers");

/** Record a payment against an order (QA: payment step). */
export interface PaymentInput {
  orderid?: number;
  method: string;        // INVOICE | COD | CARD
  status: string;        // PENDING | PAID | FAILED
  amount?: number;
  totalamount?: number;
  paymentdate?: string;
  transactionid?: string;
}
export async function createPayment(p: PaymentInput): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/payment/create`, p);
  return res?.data;
}

/** Razorpay gateway helpers (online card/UPI). */
export async function razorpayConfig(): Promise<{ enabled: boolean; keyId: string; currency: string }> {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/payment/razorpay/config`);
    return res?.data ?? { enabled: false, keyId: "", currency: "CAD" };
  } catch {
    return { enabled: false, keyId: "", currency: "CAD" };
  }
}
export async function razorpayCreateOrder(orderId: number | undefined, amount: number): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/payment/razorpay/order`, { orderId, amount });
  return res?.data;
}
export async function razorpayVerify(payload: any): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/payment/razorpay/verify`, payload);
  return res?.data;
}

/** Backend-shaped product for create/update (QA #3). */
export interface ProductInput {
  id?: number;
  name: string;
  description?: string;
  price: number;
  stock: number;
  category?: string;
  vendor?: string;
  image?: string;          // data URI (base64) or URL
  sku?: string;
  packagingUnit?: string;
  unitsPerCase?: number;
  available?: boolean;
}

export async function updateProduct(p: ProductInput): Promise<any> {
  // Backend route is /api/product/update/{id}; send the id in the path.
  const res = await axios.put(`${API_BASE_URL}/api/product/update/${p.id}`, p);
  return res?.data;
}

export async function createProduct(p: ProductInput): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/product/create`, p);
  return res?.data;
}

/** Assigned customer-specific prices for a buyer (QA #5): productId -> casePrice. */
export async function fetchCustomerPrices(userId: number): Promise<Record<number, number>> {
  try {
    const res = await axios.get<any[]>(`${API_BASE_URL}/api/pricing/customer/${userId}`);
    const map: Record<number, number> = {};
    (res?.data ?? []).forEach((cp) => {
      if (cp?.productId != null && cp?.casePrice != null) map[Number(cp.productId)] = Number(cp.casePrice);
    });
    return map;
  } catch (e) {
    console.error("GET customer prices failed", e);
    return {};
  }
}

/** A single order line sent at checkout (QA #1: lines are now persisted). */
export interface OrderLineInput {
  productId: number;
  productName: string;
  sku?: string;
  packagingUnit?: string;
  quantity: number;
  unitPrice: number;
  orderUnit?: "CASE" | "UNIT";
}

export interface NewOrderPayload {
  userId?: number | null;
  customerName?: string;
  distributorId?: number | null;
  orderDate?: string;
  status?: string;
  deliveryDate?: string;
  subtotal?: number;
  tax?: number;
  totalAmount?: number;
  items?: OrderLineInput[];
  [k: string]: any;
}

export async function createOrder(payload: NewOrderPayload): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/orders/checkout`, payload);
  return res?.data;
}

/** Fetch a single order with its line items (QA #2). */
export async function fetchOrder(id: number): Promise<any | null> {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/orders/${id}`);
    return res?.data ?? null;
  } catch (e) {
    console.error(`GET /api/orders/${id} failed`, e);
    return null;
  }
}

/** Distributor status change with validated transitions (QA #2). */
export async function updateOrderStatus(id: number, status: string): Promise<any> {
  const res = await axios.put(`${API_BASE_URL}/api/orders/${id}/status`, null, { params: { status } });
  return res?.data;
}

/** Distributor approves / suspends a buyer account (B3). */
export async function setUserStatus(id: number, status: string): Promise<any> {
  const res = await axios.put(`${API_BASE_URL}/api/user/${id}/status`, null, { params: { status } });
  return res?.data;
}

/** Account type chosen at sign-up. */
export type RegisterUserType = "BUYER" | "SELLER";

export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  phone?: string;
  /** Whether this account is a buyer or a seller. */
  userType: RegisterUserType;
  /** Optional: distributor (tenant) this buyer is registering under. */
  tenantId?: number | null;
}

/** Registration reuses the backend's user-create endpoint (no dedicated /auth/register). */
export async function register(p: RegisterPayload): Promise<any> {
  // Mirror the backend User model: { userid?, name, email, password, phone, role }.
  // The backend assigns userid/id and stores the buyer/seller choice as `role`
  // (BUYER | SELLER); the login token then carries it back as a role claim.
  const body: Record<string, unknown> = {
    name: p.name,
    email: p.email,
    password: p.password,
    phone: p.phone ?? "",
    role: p.userType,
  };
  if (p.tenantId != null) body.tenantId = p.tenantId;
  const res = await axios.post(`${API_BASE_URL}/api/auth/register`, body);
  return res?.data;
}

export interface LoginResult {
  token: string;
  /** Any role-ish value returned in the login response body (token-independent). */
  role?: unknown;
  /** Account status from the login body (PENDING | APPROVED | SUSPENDED). */
  status?: string | null;
  raw?: any;
}

export async function login(username: string, password: string): Promise<LoginResult> {
  const res = await axios.post(`${API_BASE_URL}/api/auth/login`, { username, password });
  const data = res?.data ?? {};
  const token = data.token ?? data.accessToken ?? data.jwt ?? "";
  if (token) localStorage.setItem("token", token);
  // Pull a role from the body if the backend returns one alongside the token.
  const role =
    data.role ?? data.userType ?? data.user?.role ?? data.user?.userType ?? data.roles ?? null;
  const status = data.status ?? data.user?.status ?? null;
  return { token, role, status, raw: data };
}

/**
 * Fallback role lookup: find the signed-in user in /api/user/all and read their
 * stored role. Used when neither the login body nor the JWT carries a role.
 */
export async function fetchUserRole(username: string): Promise<unknown> {
  try {
    const users = await getList("/api/user/all");
    const key = String(username).trim().toLowerCase();
    const u = (users as any[]).find((x) =>
      [x?.email, x?.username, x?.userName, x?.name]
        .filter(Boolean)
        .some((v) => String(v).trim().toLowerCase() === key)
    );
    return u ? (u.role ?? u.userType ?? null) : null;
  } catch {
    return null;
  }
}


// ---- Categories CRUD -------------------------------------------------------
// Backend category record shape: { id, categoryid, catname, image, parentId }.
// The UI works with a friendlier CategoryInput and maps to that shape here.
export interface CategoryInput {
  id?: number;
  name: string;
  image?: string | null;
  parentId?: number | null;
}

function toBackendCategory(c: CategoryInput) {
  return {
    ...(c.id ? { id: c.id, categoryid: c.id } : {}),
    catname: c.name,
    image: c.image ?? null,
    parentId: c.parentId ?? null,
  };
}

export async function createCategory(c: CategoryInput): Promise<any> {
  const res = await axios.post(`${API_BASE_URL}/api/category/create`, toBackendCategory(c));
  return res?.data;
}

export async function updateCategory(c: CategoryInput): Promise<any> {
  const res = await axios.put(`${API_BASE_URL}/api/category/update`, toBackendCategory(c));
  return res?.data;
}

export async function deleteCategory(id: number): Promise<any> {
  const res = await axios.delete(`${API_BASE_URL}/api/category/delete/${id}`);
  return res?.data;
}


/** Phase B: request a password reset link (generic response; never reveals existence). */
export async function forgotPassword(email: string): Promise<{ message?: string }> {
  const res = await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
  return res?.data ?? {};
}

/** Phase B: complete a password reset with a single-use token. */
export async function resetPassword(token: string, password: string): Promise<{ message?: string }> {
  const res = await axios.post(`${API_BASE_URL}/api/auth/reset-password`, { token, password });
  return res?.data ?? {};
}

/** Phase C: resolve a distributor (tenant) by its public slug, for storefront
 *  theming and binding a new buyer registration to the right distributor. */
export interface PublicTenant { id: number; name: string; slug?: string; primaryColor?: string; logo?: string; }
export async function resolveTenantBySlug(slug: string): Promise<PublicTenant | null> {
  try {
    const res = await axios.get(`${API_BASE_URL}/api/public/tenant/by-slug/${encodeURIComponent(slug)}`);
    return res?.data ?? null;
  } catch {
    return null;
  }
}


// ===========================================================================
// Phase D — Platform Super Admin: tenant management + inspection + view-as
// ===========================================================================

export interface TenantVM {
  id: number;
  name: string;
  slug?: string;
  customDomain?: string;
  status: string;
  primaryContact?: string;
  contactEmail?: string;
  primaryColor?: string;
  plan?: string;
  createdAt?: string;
  userCount?: number;
  buyerCount?: number;
  productCount?: number;
  orderCount?: number;
}

export const fetchTenants = (q?: string) =>
  getList(`/api/tenant/all${q ? `?q=${encodeURIComponent(q)}` : ""}`) as Promise<TenantVM[]>;

export async function fetchTenant(id: number): Promise<TenantVM | null> {
  try { return (await axios.get(`${API_BASE_URL}/api/tenant/${id}`)).data ?? null; }
  catch (e) { console.error(`GET /api/tenant/${id} failed`, e); return null; }
}
export async function createTenant(t: Partial<TenantVM>): Promise<any> {
  return (await axios.post(`${API_BASE_URL}/api/tenant/create`, t)).data;
}
export async function updateTenant(id: number, patch: Partial<TenantVM>): Promise<any> {
  return (await axios.put(`${API_BASE_URL}/api/tenant/${id}`, patch)).data;
}
export async function setTenantStatus(id: number, status: string): Promise<any> {
  return (await axios.put(`${API_BASE_URL}/api/tenant/${id}/status`, null, { params: { status } })).data;
}
export const fetchTenantCustomers = (id: number) => getList(`/api/tenant/${id}/customers`);
export const fetchTenantStaff     = (id: number) => getList(`/api/tenant/${id}/staff`);
export const fetchTenantOrders    = (id: number) => getList(`/api/tenant/${id}/orders`);
export const fetchTenantProducts  = (id: number) => getList(`/api/tenant/${id}/products`);

/** Enter read-only tenant inspection; returns the short-lived scoped token. */
export async function impersonateTenant(id: number): Promise<{ token: string; tenant: TenantVM }> {
  const res = await axios.post(`${API_BASE_URL}/api/tenant/${id}/impersonate`);
  return res.data;
}
/** Record the end of a view-as session (best-effort; auth = restored super admin). */
export async function exitImpersonationApi(id: number): Promise<void> {
  try { await axios.post(`${API_BASE_URL}/api/tenant/${id}/impersonate/exit`); } catch { /* non-fatal */ }
}

// ===========================================================================
// QA #5 — Distributor Settings (own tenant): profile, delivery, team
// ===========================================================================

export interface DistributorSettings {
  id?: number;
  name?: string;
  slug?: string;
  status?: string;
  plan?: string;
  primaryContact?: string;
  contactEmail?: string;
  supportEmail?: string;
  supportPhone?: string;
  addressLine?: string;
  logo?: string;
  primaryColor?: string;
  deliveryFee?: number | null;
  freeDeliveryThreshold?: number | null;
  deliveryLeadTimeDays?: number | null;
  deliveryNotes?: string;
}

export async function fetchDistributorSettings(): Promise<DistributorSettings | null> {
  try { return (await axios.get(`${API_BASE_URL}/api/distributor/settings`)).data ?? null; }
  catch (e) { console.error("GET /api/distributor/settings failed", e); return null; }
}
export async function updateDistributorSettings(patch: DistributorSettings): Promise<any> {
  return (await axios.put(`${API_BASE_URL}/api/distributor/settings`, patch)).data;
}
export const fetchDistributorTeam = () => getList("/api/distributor/team");
export async function addStaff(p: { name: string; email: string; password: string; phone?: string; role?: string }): Promise<any> {
  return (await axios.post(`${API_BASE_URL}/api/distributor/team`, p)).data;
}
export async function setStaffStatus(id: number, status: string): Promise<any> {
  return (await axios.put(`${API_BASE_URL}/api/distributor/team/${id}/status`, null, { params: { status } })).data;
}

/** QA #2: distributor internal notes on an order. */
export async function setOrderNotes(id: number, notes: string): Promise<any> {
  return (await axios.put(`${API_BASE_URL}/api/orders/${id}/notes`, { notes })).data;
}
